document.addEventListener('DOMContentLoaded', () => {
  const youtubeServiceNames = ['YouTube Video', 'YouTube'];
  const consentState = {};
  let pendingMedia = null;
  let consentCheckInterval = null;
  let isCheckingConsent = false;

  const addAutoplay = (url) => {
    try {
      const autoplayUrl = new URL(url, window.location.href);

      autoplayUrl.searchParams.set('autoplay', '1');
      autoplayUrl.searchParams.set('rel', '0');

      return autoplayUrl.toString();
    } catch {
      const separator = url.includes('?') ? '&' : '?';

      return `${url}${separator}autoplay=1&rel=0`;
    }
  };

  const resetMedia = (media) => {
    const iframe = media.querySelector('[data-youtube-video-src]');

    media.classList.remove('youtube-videos__media--active');
    media.classList.remove('youtube-videos__media--consent-required');

    if (iframe) {
      iframe.removeAttribute('src');
    }
  };

  const getServiceNames = (iframe) => {
    const serviceName = iframe?.getAttribute('data-youtube-video-service');

    return serviceName ? [serviceName, ...youtubeServiceNames.filter((name) => name !== serviceName)] : youtubeServiceNames;
  };

  const normalizeName = (name) => String(name || '').trim().toLowerCase();

  const isYoutubeService = (name, serviceNames = youtubeServiceNames) => {
    const normalizedName = normalizeName(name);

    return serviceNames.some((serviceName) => normalizeName(serviceName) === normalizedName)
      || normalizedName.includes('youtube');
  };

  const readConsentStatus = (service) => {
    const candidates = [
      service?.consent,
      service?.consent?.status,
      service?.consent?.given,
      service?.consentStatus,
      service?.accepted,
      service?.isAccepted,
      service?.status,
    ];

    return candidates.find((value) => typeof value === 'boolean') ?? null;
  };

  const getServicesFromMethod = (methodName) => {
    if (typeof window.UC_UI?.[methodName] !== 'function') {
      return [];
    }

    try {
      const services = window.UC_UI[methodName]();

      return Array.isArray(services) ? services : [];
    } catch {
      return [];
    }
  };

  const getUsercentricsServices = () => [
    ...getServicesFromMethod('getServicesBaseInfo'),
    ...getServicesFromMethod('getServices'),
  ];

  const getUsercentricsService = (serviceNames) => getUsercentricsServices()
    .find((service) => isYoutubeService(service.name, serviceNames)) || null;

  const getConsentFromEventDetail = (detail) => {
    if (!detail || typeof detail !== 'object') {
      return null;
    }

    const directStatus = Object.entries(detail).find(([name, value]) => (
      typeof value === 'boolean'
      && isYoutubeService(name)
    ));

    if (directStatus) {
      return directStatus[1];
    }

    if (
      isYoutubeService(detail.name)
      || isYoutubeService(detail.templateId)
      || isYoutubeService(detail.id)
    ) {
      return readConsentStatus(detail);
    }

    const services = [
      ...(Array.isArray(detail.services) ? detail.services : []),
      ...(Array.isArray(detail.consents) ? detail.consents : []),
    ];
    const matchingService = services.find((service) => (
      isYoutubeService(service?.name)
      || isYoutubeService(service?.templateId)
      || isYoutubeService(service?.id)
    ));

    return readConsentStatus(matchingService);
  };

  const hasConsent = (iframe) => {
    const serviceNames = getServiceNames(iframe);
    const usercentricsService = getUsercentricsService(serviceNames);

    if (usercentricsService?.consent?.status === true) {
      return true;
    }

    return serviceNames.some((serviceName) => consentState[serviceName] === true)
      || Object.entries(consentState).some(([serviceName, consent]) => consent === true && isYoutubeService(serviceName, serviceNames));
  };

  const hasConsentAsync = async (iframe) => {
    if (hasConsent(iframe)) {
      return true;
    }

    if (typeof window.UC_UI?.getServicesFullInfo !== 'function') {
      return false;
    }

    try {
      const services = await window.UC_UI.getServicesFullInfo();
      const service = Array.isArray(services)
        ? services.find((item) => isYoutubeService(item.name, getServiceNames(iframe)))
        : null;

      return readConsentStatus(service) === true;
    } catch {
      return false;
    }
  };

  const openConsentSettings = (iframe) => {
    const service = getUsercentricsService(getServiceNames(iframe));

    if (service?.id && typeof window.UC_UI?.showSecondLayer === 'function') {
      try {
        window.UC_UI.showSecondLayer(service.id);

        return;
      } catch {
        // Fall through to the general privacy settings layer.
      }
    }

    if (typeof window.UC_UI?.showSecondLayer === 'function') {
      window.UC_UI.showSecondLayer();

      return;
    }

    if (typeof window.UC_UI?.showFirstLayer === 'function') {
      window.UC_UI.showFirstLayer();
    }
  };

  const playMedia = (media) => {
    const iframe = media.querySelector('[data-youtube-video-src]');
    const src = iframe?.getAttribute('data-youtube-video-src');

    if (!iframe || !src) {
      return;
    }

    iframe.setAttribute('src', addAutoplay(src));
    media.classList.remove('youtube-videos__media--consent-required');
    media.classList.add('youtube-videos__media--active');
  };

  const stopConsentWatcher = () => {
    if (consentCheckInterval) {
      window.clearInterval(consentCheckInterval);
      consentCheckInterval = null;
    }
  };

  const playPendingMediaAfterConsent = async () => {
    if (!pendingMedia || isCheckingConsent) {
      return;
    }

    const media = pendingMedia;
    const iframe = media.querySelector('[data-youtube-video-src]');

    if (!iframe) {
      pendingMedia = null;

      return;
    }

    isCheckingConsent = true;

    try {
      if (await hasConsentAsync(iframe)) {
        playMedia(media);
        pendingMedia = null;
        stopConsentWatcher();
      }
    } finally {
      isCheckingConsent = false;
    }
  };

  const startConsentWatcher = () => {
    let attempts = 0;

    stopConsentWatcher();
    consentCheckInterval = window.setInterval(() => {
      attempts += 1;
      void playPendingMediaAfterConsent();

      if (attempts >= 60) {
        stopConsentWatcher();
      }
    }, 500);

    void playPendingMediaAfterConsent();
  };

  const requireConsent = (media) => {
    const iframe = media.querySelector('[data-youtube-video-src]');

    pendingMedia = media;
    iframe?.removeAttribute('src');
    media.classList.add('youtube-videos__media--consent-required');
    media.classList.remove('youtube-videos__media--active');
    startConsentWatcher();
  };

  const handleConsentEvent = (event) => {
    const consentStatus = getConsentFromEventDetail(event.detail);

    if (typeof consentStatus === 'boolean') {
      youtubeServiceNames.forEach((serviceName) => {
        consentState[serviceName] = consentStatus;
      });
    }

    void playPendingMediaAfterConsent();
  };

  window.addEventListener('ucEvent', handleConsentEvent);
  window.addEventListener('UC_UI_CMP_EVENT', handleConsentEvent);
  window.addEventListener('UC_UI_INITIALIZED', () => {
    void playPendingMediaAfterConsent();
  });
  window.addEventListener('focus', () => {
    void playPendingMediaAfterConsent();
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      void playPendingMediaAfterConsent();
    }
  });

  document.querySelectorAll('[data-youtube-video-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const media = trigger.closest('.youtube-videos__media--iframe');
      const container = trigger.closest('.youtube-videos');
      const iframe = media?.querySelector('[data-youtube-video-src]');
      const src = iframe?.getAttribute('data-youtube-video-src');

      if (!media || !iframe || !src) {
        return;
      }

      container?.querySelectorAll('.youtube-videos__media--iframe.youtube-videos__media--active').forEach((activeMedia) => {
        if (activeMedia !== media) {
          resetMedia(activeMedia);
        }
      });

      if (hasConsent(iframe)) {
        playMedia(media);
      } else {
        requireConsent(media);
      }
    });
  });

  document.querySelectorAll('[data-youtube-consent-settings]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const media = trigger.closest('.youtube-videos__media--iframe');

      if (media) {
        pendingMedia = media;
        openConsentSettings(media.querySelector('[data-youtube-video-src]'));
        startConsentWatcher();
      }
    });
  });
});
